////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: About route — hero, intro, MD profile, capabilities, partners  //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { usePageTitle } from '@/hooks/usePageTitle';
import {
  getPage,
  splitSections,
  stripImages,
  extractImages,
  parseImageTextPairs,
  parseProfileSection,
} from '@/lib/content';
import { Markdown } from '@/components/content/Markdown';
import { Img } from '@/components/media/Img';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal, RevealGroup, RevealItem } from '@/components/content/Reveal';
import { clipWipe } from '@/lib/motion';

// MARK — discovery §2.1 lists these six capability labels in this order, matching
// the six `capabilities-*.jpg` asset filenames (Planning, Design, Cost,
// Preconstruction, Delivery, Handover) — but the images actually inlined in
// about.md's Capabilities section are a different set (`Ironside_Small_Carousel_*`).
// Labels below are assigned by section order per the discovery note; not verified
// against the live site by this pass. Flag for ratification.
const CAPABILITY_LABELS = ['Planning', 'Design', 'Cost', 'Preconstruction', 'Delivery', 'Handover'];

// MARK — partner/client names inferred from filenames only (discovery §2.1, in the
// SAME order the images appear in about.md's Partners section). Per 1.0.2.md §5.2
// and LEDGER.md open decision #6: have marketing confirm this list before it ships —
// publishing a client's logo under the wrong name is a phone call nobody wants.
// NOTE — partner/vendor logo images currently live under /scrape/about/ (interim,
// per 1.0.2.md §0). Once migrated off the scrape path, they belong under
// public/vendors/ per Mike's fixed-asset naming convention (10/08/2026) — IRONSIDE's
// own logos/icons use gui/public/ root; third-party vendor/partner logos use
// public/vendors/.
const PARTNER_NAMES = [
  'Milieu', 'Hip V.', 'Cedarwoods', 'Glenvill', 'Caydon', 'Beulah', 'Ascui', 'Webber',
  'Lechte', 'DKO', 'Poly', 'First Urban', 'M2', 'Trevaskis', 'Stantec', 'ADP', 'Mod',
  'Nash', 'White Sétia', 'PAN', 'Grand Land', 'PDS Group', 'Odo', 'Vantage Point', 'Pomeroy',
];

export function About () {
  usePageTitle('About');

  const page = getPage('about');
  if (!page) return null; // Content pipeline guarantees this slug exists; see lib/content.ts.

  const sections = splitSections(page.bodyMd);
  const intro = sections.find((s) => s.id === 'intro');
  const introduction = sections.find((s) => s.heading === 'Introduction');
  const managingDirector = sections.find((s) => s.heading === 'Managing Director');
  const capabilities = sections.find((s) => s.heading === 'Capabilities');
  const partners = sections.find((s) => s.heading === 'Partners');

  // MARK — 1.0.3: the intro's scraped hero photo is no longer rendered inline
  // as a full-width <img> — the page's own persistent <BackgroundLayer />
  // (PublicLayout, keyed on route) already carries the /about background per
  // 1.0.3.md §4/§5's "free-floating title over its own background" contract, and
  // a second, page-local hero image would fight it rather than complement it.
  // The tagline copy that used to sit under that image is unchanged, just moved
  // into <PageHero>'s subtitle slot.
  const heroTagline = intro ? stripImages(intro.bodyMd) : '';
  const profile = managingDirector ? parseProfileSection(managingDirector.bodyMd) : null;
  const capabilityPairs = capabilities ? parseImageTextPairs(capabilities.bodyMd) : [];
  const partnerImages = partners ? extractImages(partners.bodyMd) : [];

  return (
    <div>
      <PageHero title="About" subtitle={heroTagline || undefined} />

      {introduction && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="introduction" aria-labelledby="introduction-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="introduction-heading" className="text-2xl font-semibold text-ink sm:text-4xl">Introduction</h2>
              <Markdown source={introduction.bodyMd} className="mt-6" />
            </GlassTile>
          </Reveal>
        </section>
      )}

      {profile && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="managing-director" aria-labelledby="md-heading">
          <Reveal as="section">
            {/* Long-form bio copy — the quieter "prose" tile finish (1.0.3.md
                §6, styles/glass.css's `.glass-tile--prose`), not the heavier-
                transparency default. */}
            <GlassTile className="glass-tile--prose p-8 sm:p-12">
              <h2 id="md-heading" className="text-2xl font-semibold text-ink">Managing Director</h2>
              <div className="mt-6 flex flex-col gap-8 sm:flex-row">
                {profile.image && (
                  <Img
                    src={profile.image.src}
                    alt={`${profile.name || 'Managing Director'} — portrait`}
                    className="h-64 w-48 shrink-0 rounded object-cover"
                  />
                )}
                <div>
                  {profile.name && <h3 className="text-xl font-semibold text-ink">{profile.name}</h3>}
                  <Markdown source={profile.bioMd} className="mt-4" />
                </div>
              </div>
            </GlassTile>
          </Reveal>
        </section>
      )}

      {capabilityPairs.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="capabilities" aria-labelledby="capabilities-heading">
          <Reveal as="div" className="mb-8">
            <h2 id="capabilities-heading" className="text-2xl font-semibold text-ink">Capabilities</h2>
          </Reveal>
          <RevealGroup as="div" className="space-y-12">
            {capabilityPairs.map((pair, i) => (
              <RevealItem key={pair.image} as="div">
                <GlassTile className="grid grid-cols-1 gap-6 p-6 sm:grid-cols-2 sm:items-center sm:p-8">
                  <div className="overflow-hidden rounded">
                    <Img
                      src={pair.image}
                      alt={`Ironside capability — ${CAPABILITY_LABELS[i] ?? `Capability ${i + 1}`}`}
                      className="w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-ink">{CAPABILITY_LABELS[i] ?? `Capability ${i + 1}`}</h3>
                    <Markdown source={pair.text} className="mt-2" />
                  </div>
                </GlassTile>
              </RevealItem>
            ))}
          </RevealGroup>
        </section>
      )}

      {partnerImages.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="partners" aria-labelledby="partners-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="partners-heading" className="text-2xl font-semibold text-ink">Partners</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted">
                Client and partner names below are inferred from image filenames and awaiting
                marketing confirmation before launch (LEDGER.md open decision #6).
              </p>
              <RevealGroup as="ul" className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-4 md:grid-cols-6">
                {partnerImages.map((image, i) => (
                  <RevealItem key={image.src} as="li" variants={clipWipe} className="flex items-center justify-center">
                    <Img
                      src={image.src}
                      alt={PARTNER_NAMES[i] ?? `Partner ${i + 1}`}
                      className="h-16 w-full object-contain"
                    />
                  </RevealItem>
                ))}
              </RevealGroup>
            </GlassTile>
          </Reveal>
        </section>
      )}
    </div>
  );
}
